import { useState } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useApi, asArray } from '../../hooks/useApi'
import { ContentHeader } from '../../components/layout/ContentHeader'
import { Download, X, ChevronDown } from 'lucide-react'

interface PageIssue { type: string; title: string; severity: 'critical' | 'warning' | 'info' }
interface Page {
  id: number
  url: string
  statusCode: number
  title: string | null
  wordCount: number | null
  depth: number | null
  issueCount?: number
  maxSeverity?: 'critical' | 'warning' | 'info' | null
  issues?: PageIssue[]
}
interface AuditData { id: number; status: string }

const SEVERITY_COLORS: Record<string, { badge: string; text: string }> = {
  critical: { badge: 'bg-red-500/15 text-red-500', text: 'text-red-500' },
  warning: { badge: 'bg-amber-500/15 text-amber-500', text: 'text-amber-500' },
  info: { badge: 'bg-cyan-500/15 text-cyan-500', text: 'text-cyan-500' },
}

export default function AuditPages() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [openDropdown, setOpenDropdown] = useState<number | null>(null)

  const hasIssues = searchParams.get('hasIssues')
  const issueFilter = searchParams.get('issue')
  const severityFilter = searchParams.get('severity')
  const assetsFilter = searchParams.get('assets')

  const activeTab = assetsFilter === 'true' ? 'assets' : 'html'

  const { data: audits } = useApi<AuditData[]>(`/api/clients/${projectId}/audits`)
  const auditList = Array.isArray(audits) ? audits : []; const latestAudit = auditList.find(a => a.status === 'completed')
  const auditId = latestAudit?.id ? String(latestAudit.id) : null

  // Build API URL with filters
  let apiUrl = auditId ? `/api/audits/${auditId}/pages` : '/api/health'
  if (auditId) {
    const params = new URLSearchParams()
    if (hasIssues === 'true') params.set('hasIssues', 'true')
    if (issueFilter) params.set('issue', issueFilter)
    if (severityFilter) params.set('severity', severityFilter)
    if (assetsFilter === 'true') params.set('assets', 'true')
    const qs = params.toString()
    if (qs) apiUrl += `?${qs}`
  }

  const { data: pages, loading } = useApi<Page[]>(apiUrl)

  const statusColor = (code: number) => {
    if (code >= 200 && code < 300) return 'text-score-excellent'
    if (code >= 300 && code < 400) return 'text-score-average'
    return 'text-score-critical'
  }

  const removeFilter = (key: string) => {
    const next = new URLSearchParams(searchParams)
    next.delete(key)
    setSearchParams(next)
  }

  const setTab = (tab: 'html' | 'assets') => {
    const next = new URLSearchParams(searchParams)
    if (tab === 'assets') {
      next.set('assets', 'true')
    } else {
      next.delete('assets')
    }
    setSearchParams(next)
  }

  const activeFilters: { key: string; label: string }[] = []
  if (hasIssues === 'true') activeFilters.push({ key: 'hasIssues', label: 'Avec erreurs' })
  if (issueFilter) activeFilters.push({ key: 'issue', label: `Issue: ${issueFilter}` })
  if (severityFilter) activeFilters.push({ key: 'severity', label: `Severite: ${severityFilter}` })

  return (
    <>
      <ContentHeader
        breadcrumb={[{ label: 'Audit de site Web', to: `/p/${projectId}/audit` }, { label: 'Pages crawlees' }]}
        title="Pages crawlees"
        subtitle={pages ? `${asArray(pages).length} pages` : undefined}
        actions={auditId ? (
          <button onClick={() => window.open(`/api/audits/${auditId}/pages?format=csv`, '_blank')} className="flex items-center gap-2 px-3 py-2 bg-white border border-[#E2E4ED] rounded-xl text-sm text-[#8B8FA7] hover:text-[#1B2559] hover:border-[#C8CBD8] transition-colors">
            <Download className="w-4 h-4" strokeWidth={1.25} />
            Export CSV
          </button>
        ) : undefined}
      />

      {/* Tabs + Active filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex bg-[#F4F5FA] rounded-lg p-0.5">
          <button
            onClick={() => setTab('html')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === 'html' ? 'bg-white text-[#1B2559] shadow-sm' : 'text-[#8B8FA7] hover:text-[#1B2559]'}`}
          >
            Pages HTML
          </button>
          <button
            onClick={() => setTab('assets')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === 'assets' ? 'bg-white text-[#1B2559] shadow-sm' : 'text-[#8B8FA7] hover:text-[#1B2559]'}`}
          >
            Assets
          </button>
        </div>

        {activeFilters.map(f => (
          <button
            key={f.key}
            onClick={() => removeFilter(f.key)}
            className="flex items-center gap-1.5 px-2.5 py-1 bg-orion-primary/10 text-orion-primary text-xs font-medium rounded-full hover:bg-orion-primary/20 transition-colors"
          >
            {f.label}
            <X className="w-3 h-3" strokeWidth={2} />
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-orion-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && asArray(pages).length > 0 && (
        <div className="bg-white border border-[#E2E4ED] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F4F5FA] text-[#8B8FA7] text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3 font-medium">URL</th>
                <th className="text-center px-4 py-3 font-medium w-20">Statut</th>
                <th className="text-left px-4 py-3 font-medium">Title</th>
                <th className="text-center px-4 py-3 font-medium w-24">Mots</th>
                <th className="text-center px-4 py-3 font-medium w-28">Issues</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E4ED]">
              {asArray(pages).map(page => {
                const issueCount = page.issueCount ?? 0
                const severity = page.maxSeverity
                const sevConfig = severity ? SEVERITY_COLORS[severity] : null
                const isOpen = openDropdown === page.id

                return (
                  <tr key={page.id} className="hover:bg-[#F4F5FA] transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-orion-primary-text truncate max-w-md">{page.url}</td>
                    <td className={`px-4 py-3 text-center font-mono font-bold ${statusColor(page.statusCode)}`}>{page.statusCode}</td>
                    <td className="px-4 py-3 text-[#8B8FA7] truncate max-w-xs">{page.title ?? '-'}</td>
                    <td className="px-4 py-3 text-center font-mono text-[#8B8FA7]">{page.wordCount ?? '-'}</td>
                    <td className="px-4 py-3 text-center relative">
                      {issueCount === 0 ? (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-500">OK</span>
                      ) : (
                        <div className="relative inline-block">
                          <button
                            onClick={() => setOpenDropdown(isOpen ? null : page.id)}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${sevConfig?.badge ?? 'bg-gray-100 text-gray-500'}`}
                          >
                            {issueCount}
                            <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} strokeWidth={2} />
                          </button>
                          {isOpen && page.issues && (
                            <div className="absolute right-0 top-full mt-1 z-20 w-64 bg-white border border-[#E2E4ED] rounded-xl shadow-lg py-1">
                              {page.issues.map((issue, idx) => {
                                const issueSev = SEVERITY_COLORS[issue.severity] ?? SEVERITY_COLORS.info
                                return (
                                  <button
                                    key={idx}
                                    onClick={() => {
                                      setOpenDropdown(null)
                                      navigate(`/p/${projectId}/audit/issues/${issue.type}?auditId=${auditId}`)
                                    }}
                                    className="w-full text-left px-3 py-2 hover:bg-[#F4F5FA] transition-colors flex items-center gap-2"
                                  >
                                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${issueSev.text.replace('text-', 'bg-')}`} />
                                    <span className="text-xs text-[#1B2559] truncate">{issue.title}</span>
                                  </button>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
